<script setup lang="ts">
  import { KEY_ENTER, KEY_SPACE } from '@travelata-libs/utils';
  import { computed, type InputHTMLAttributes } from 'vue';
  import { CheckboxSize } from './Checkbox.types';
  import { Icon, Typo, IconName, TypoVariant } from '@/components';

  const TEXT_VARIANT_BY_SIZE = {
    [CheckboxSize.S]: TypoVariant.BodySRegular,
    [CheckboxSize.L]: TypoVariant.BodyLRegular,
    [CheckboxSize.M]: TypoVariant.BodyMRegular,
    [CheckboxSize.XL]: TypoVariant.BodyXLRegular,
  };

  interface CheckboxProps {
    modelValue: boolean;
    label?: string;
    disabled?: boolean;
    error?: boolean;
    indeterminate?: boolean;
    size?: `${CheckboxSize}`;
    value?: InputHTMLAttributes['value'];
    name?: InputHTMLAttributes['name'];
    id?: InputHTMLAttributes['id'];
  }

  interface CheckboxEmits {
    (e: 'update:modelValue', value: boolean): void;
  }

  const props = withDefaults(defineProps<CheckboxProps>(), {
    label: '',
    disabled: false,
    size: CheckboxSize.M,
    indeterminate: false,
    value: '',
    name: '',
    id: '',
  });

  const emit = defineEmits<CheckboxEmits>();

  const innerValue = computed({
    get() {
      return props.modelValue;
    },
    set(value: boolean) {
      emit('update:modelValue', value);
    },
  });

  const iconName = computed(() => {
    if (props.indeterminate && !innerValue.value) {
      return IconName.Dash;
    }
    return IconName.Check;
  });

  const textVariant = computed(() => {
    return TEXT_VARIANT_BY_SIZE[props.size];
  });

  const toggle = () => {
    innerValue.value = !innerValue.value;
  };

  const onKeyDown = (event: KeyboardEvent) => {
    if (!props.disabled && [KEY_ENTER, KEY_SPACE].includes(event.key)) {
      event.preventDefault();
      toggle();
    }
  };
</script>

<script lang="ts">
  export default {
    model: {
      prop: 'modelValue',
      event: 'update:modelValue',
    },
  };
</script>

<template>
  <label
    :class="[
      'tl-checkbox',
      `tl-checkbox_size-${size}`,
      {
        'tl-checkbox_checked': innerValue,
        'tl-checkbox_disabled': disabled,
        'tl-checkbox_error': error,
        'tl-checkbox_indeterminate': indeterminate,
      },
    ]"
    role="checkbox"
    :aria-checked="innerValue"
    :aria-disabled="disabled"
    @keydown="onKeyDown"
  >
    <input
      :id="id"
      v-model="innerValue"
      data-test-id="input"
      class="tl-checkbox__input"
      type="checkbox"
      :disabled="disabled"
      :name="name"
      :value="value"
      tabindex="-1"
    />
    <span class="tl-checkbox__frame" :tabindex="disabled ? -1 : 0">
      <Icon
        :class="['tl-checkbox__icon', { 'tl-checkbox__icon_visible': innerValue || indeterminate }]"
        :name="iconName"
        size="xs"
        data-test-id="icon"
      />
    </span>
    <span v-if="$slots.default || label" class="tl-checkbox__content">
      <slot>
        <Typo :variant="textVariant" class="tl-checkbox__label" data-test-id="label">{{ label }}</Typo>
      </slot>
    </span>
  </label>
</template>

<style lang="scss" scoped>
  .tl-checkbox {
    $root: &;

    cursor: pointer;
    display: inline-flex;
    gap: var(--spacing-xs);
    transition: var(--transition-default);
    user-select: none;

    @include disable-tap-highlight;

    &:hover {
      opacity: 0.7;
    }

    &_error {
      #{$root}__frame {
        background-color: var(--color-error-100);
        border-color: var(--color-error-900);
      }
    }

    &_disabled {
      cursor: default;

      #{$root}__frame {
        background-color: var(--color-disabled-500);
        border-color: var(--color-disabled-900);
      }

      #{$root}__content {
        color: var(--color-disabled-900);
      }

      &#{$root}_checked,
      &#{$root}_indeterminate {
        #{$root}__frame {
          background-color: var(--color-disabled-900);
          border-color: var(--color-disabled-900);
        }
      }
    }

    &_indeterminate,
    &_checked {
      #{$root}__frame {
        background-color: var(--color-brand-secondary);
        border-color: var(--color-brand-secondary);
      }
    }

    &_size {
      &-s,
      &-m {
        #{$root}__frame {
          height: 16px;
          width: 16px;
        }
      }

      &-l,
      &-xl {
        #{$root}__frame {
          height: 20px;
          width: 20px;
        }
      }

      &-s {
        #{$root}__content {
          @include typography-body(s, 400);
        }
      }

      &-m {
        #{$root}__content {
          @include typography-body(m, 400);
        }
      }

      &-l {
        #{$root}__content {
          @include typography-body(l, 400);
        }
      }

      &-xl {
        #{$root}__content {
          @include typography-body(xl, 400);
        }
      }
    }

    &__input {
      height: 0;
      margin: 0;
      opacity: 0;
      outline: none;
      padding: 0;
      position: absolute;
      width: 0;
      z-index: -1;
    }

    &__frame {
      align-items: center;
      background-color: var(--color-bg-primary);
      border: 1px solid var(--color-text-secondary);
      border-radius: var(--rounding-s);
      box-sizing: border-box;
      display: flex;
      flex-shrink: 0;
      justify-content: center;
    }

    &__icon {
      fill: var(--color-text-inverse);
      visibility: hidden;

      &_visible {
        visibility: visible;
      }
    }

    &__content {
      align-items: center;
      color: var(--color-text-primary);
      display: flex;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      width: 100%;
    }

    &__label {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      width: 100%;
    }
  }
</style>
